import React from 'react'
import { accessibleStatusColor, surfaceForMode, opaqueStatusTint } from '../../utils/statusColor'
import { useThemeContext } from '../../theme/ThemeContext'

const shimmerStyle = {
  background: 'linear-gradient(90deg, transparent 0%, rgba(128,128,128,0.15) 20%, rgba(128,128,128,0.25) 50%, rgba(128,128,128,0.15) 80%, transparent 100%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
}

const StatusTabsSkeleton = ({ count = 3 }) => (
  <>
    <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    <div className="flex gap-3 flex-wrap">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg h-[38px] w-[120px]"
          style={shimmerStyle}
        />
      ))}
    </div>
  </>
)

const StatusTabs = ({ counts = {}, statuses = [], activeStatus, onStatusChange, loading = false, skeletonCount, outlined = false, hideTotal = false, leadingTabs = [] }) => {
  // Read before the loading early-return: hooks cannot sit behind a conditional return.
  const { mode } = useThemeContext()
  const { dark, surface } = surfaceForMode(mode)

  if (loading) return <StatusTabsSkeleton count={skeletonCount || statuses.length + 1 || 3} />

  const tabs = [
    ...(hideTotal ? [] : [{ label: 'Total', value: '', count: counts.total || 0, neutral: true }]),
    ...leadingTabs.map(t => ({ label: t.label, value: t.value, count: t.count || 0, neutral: true })),
    ...statuses.map(s => ({
      label: s.label,
      value: s.value,
      count: counts[s.value] || 0,
      color: s.textColor || s.bgColor || '#667085',
    }))
  ]

  return (
    <div className="flex gap-3 flex-wrap">
      {tabs.map(tab => {
        const isActive = activeStatus === tab.value
        // Translucent tint of the (solid) status color so tabs adapt to light AND dark;
        // active state = stronger tint + a ring in the status color. "Total" = neutral token.
        let style
        if (tab.neutral) {
          style = {
            backgroundColor: isActive ? 'var(--color-gray-300)' : 'var(--color-gray-200)',
            color: 'var(--color-text-primary)',
            boxShadow: isActive ? 'inset 0 0 0 1.5px var(--color-gray-400)' : 'none',
          }
        } else {
          // Status colours come from the API and some of them are not legible at pill
          // size: on /leads, "Contract Sent" (#4F46E5) measured 2.50:1 on its own 12%
          // tint. StatusBadge already corrects this; these tabs painted tab.color raw,
          // so the SAME status read 6.00:1 as a badge and 2.50:1 as a tab. The alpha
          // has to be passed through, or the correction aims at the wrong backdrop.
          const alpha = isActive ? 0.24 : 0.12
          const legible = accessibleStatusColor(tab.color, { dark, surface, alpha })
          // opaque, so a highlighted row underneath cannot shift the pill's contrast
          const tint = opaqueStatusTint(legible, alpha, surface) || 'var(--color-gray-200)'
          style = {
            backgroundColor: tint,
            color: legible,
            boxShadow: isActive ? `inset 0 0 0 1.5px ${legible}` : 'none',
          }
        }
        const sizeClasses = outlined ? 'px-3 py-1 text-sm' : 'px-4 py-2 text-sm'
        const showDot = !tab.neutral
        return (
          <button
            key={tab.value}
            onClick={() => onStatusChange(tab.value)}
            style={style}
            // No opacity on the inactive state. The tint above is opaque and the label
            // is corrected to exactly 4.5:1 against it, and an `opacity` class composites
            // BOTH toward the card, undoing the correction one line after it is made —
            // measured 4.51 -> 3.41 in the browser. Active is already distinguished three
            // other ways (a 0.24 tint against 0.12, the inset ring, and shadow-md), so the
            // veil was redundant as well as harmful. Hover moves to a shadow, which sits
            // outside the pill and cannot dilute what is drawn inside it.
            className={`${sizeClasses} rounded-full font-semibold transition-all cursor-pointer inline-flex items-center gap-1.5 ${
              isActive ? 'shadow-md' : 'hover:shadow-sm'
            }`}
          >
            {showDot && (
              <span
                className="inline-block rounded-full"
                style={{ width: '6px', height: '6px', backgroundColor: tab.color }}
              />
            )}
            <span>{tab.label}: {tab.count}</span>
          </button>
        )
      })}
    </div>
  )
}

export default StatusTabs
